import { FC, useState } from "react";
import ModalHeader from "./ModalHeader";
import DateAndTimePicker from "./DateAndTimePicker";
import AddClient from "./AddClient";
import Products from "./Products";
import TotalPrice from "./TotalPrice";
import { Button, message, Spin } from "antd";
import PaymentMethod from "./PaymentMethod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { addAppointment } from "../../../../network/appointment";
import { formatDateToYYYYMMDD } from "../../../../utils/DateUtils";
import {
  appointmentPackage,
  appointmentService,
  client,
} from "../../../../types/calendar";
import { useSelector } from "react-redux";
import queryClient from "../../../../services/react-query/queryClient";
import { useIntl } from "react-intl";
import ReservationDetails from "./ReservationDetails";
import AppointmentModalFooter from "./AppointmentModalFooter";
import { getAppointment } from "../../../../network/calendar";
import Tax from "./Tax";
import ServicesAndPackages from "./servicesAndPackages/ServicesAndPackages";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import Discount from "./Discount";
import LoyaltyDiscountSelect from "./LoyaltyDiscountSelect";

interface ModalBodyProps {
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  dateAndTime: {
    date: Date | null;
    startTime: string | null;
    finishTime: string | null;
  };
  setDateAndTime: React.Dispatch<
    React.SetStateAction<{
      date: Date | null;
      startTime: string | null;
      finishTime: string | null;
    }>
  >;
  appointment?: number;
  Location?: any;
}

const divider = <hr className=" bg-[#00086D]  " />;

const AddAppointmentModalWrapper: FC<ModalBodyProps> = ({
  setIsModalOpen,
  dateAndTime,
  setDateAndTime,
  appointment,
}) => {
  const intl = useIntl();
  const { locationId, currentLang } = useSelector(
    (state: {
      calendar: { locationId: number | null };
      user: { currentLang: "ar" | "en" };
    }) => ({
      locationId: state.calendar.locationId,
      currentLang: state.user.currentLang,
    })
  );
  const [clientData, setClientData] = useState<client | null>(null);
  const [services, setServices] = useState<appointmentService[]>([]);
  const [packages, setPackages] = useState<appointmentPackage[]>([]);
  const [products, setProducts] = useState<
    { name: string; price: string; id: number; qty: number }[]
  >([]);
  const [tax, setTax] = useState<null | {
    id: number;
    name: string;
    tax_rate: number;
  }>(null);
  const [paymentMethod, setPaymentMethod] = useState<number | null>(null);

  const [loyaltyDiscount, setLoyaltyDiscount] = useState<{
    id: number;
    discount_percentage: string;
  } | null>(null);
  const [discount, setDiscount] = useState<{
    type: "fixed" | "percentage";
    value: number;
  }>({
    type: "fixed",
    value: 0,
  });

  const [step, setStep] = useState(appointment ? 2 : 1);
  const [appointmentId, setAppointmentId] = useState<number | null>(
    appointment || null
  );
  const mutateAppointment = useMutation(addAppointment);

  const validateTeamMembers = () => {
    if (services.length === 0 && packages.length === 0) {
      return false;
    }
    let flag: boolean = true;
    services.forEach((service) => {
      if (!service.teamMemberId) {
        flag = false;
      }
    });
    packages.forEach((pkg) => {
      if (!pkg.teamMemberId) {
        flag = false;
      }
    });
    return flag;
  };
  const handleSubmitAppointment = (save?: string) => {
    if (clientData === null) {
      message.error(
        intl.formatMessage({
          id: "clientRequired",
          defaultMessage: " يجب تحديد العميل",
        })
      );
      return;
    }
    if (!validateTeamMembers()) {
      message.error(
        intl.formatMessage({
          id: "servicesAndPackagesRequired",
        })
      );
      return;
    }
    if (dateAndTime.date === null || dateAndTime.startTime === null) {
      message.error(
        intl.formatMessage({
          id: "timeRequired",
          defaultMessage: " يجب تحديد التاريخ والساعه",
        })
      );
      return;
    }

    const data: any = {
      client_id: clientData.id,
      date: formatDateToYYYYMMDD(new Date(dateAndTime?.date)),
      start_time: dateAndTime?.startTime,
      services: services?.map((s) => ({
        pricing_option_id: s?.id,
        team_member_id: s?.teamMemberId,
      })),
      packages: packages?.map((pkg) => ({
        package_id: pkg?.id,
        team_member_id: pkg?.teamMemberId,
      })),
      products_ids: products?.map((p) => {
        return { id: p?.id, quantity: p?.qty };
      }),
      // tips: tips,
      payment_method_id: paymentMethod,
      locationId,
    };
    if (tax && tax?.id !== 0 && tax?.tax_rate !== 0) {
      data.taxable_id = tax?.id;
      data.taxable_type = "tax";
    }
    if (discount) {
      data.discount = discount?.value;
      data.discount_type = discount?.type;
    }
    if (loyaltyDiscount) {
      data.client_loyalty_id = loyaltyDiscount?.id;
    }
    mutateAppointment.mutate(data, {
      onSuccess: (res: any) => {
        if (res?.data?.data?.id) {
          setStep(5);
          queryClient.invalidateQueries(["appointments"]);
          message.success(
            intl.formatMessage({
              id: "successAppointment",
              defaultMessage: "تم الحجز بنجاح",
            })
          );
          setAppointmentId(res.data.data.id);
        } else {
          setStep(2);
          message.error(
            res?.response?.data?.message ||
              intl.formatMessage({
                id: "somethingWentWrong",
                defaultMessage: "حدث خطأ",
              })
          );
        }
      },
      onError: (error) => {
        message.error(
          intl.formatMessage({
            id: "somethingWentWrong",
            defaultMessage: "حدث خطأ",
          })
        );

        setStep(2);
      },
      onSettled: () => {
        queryClient.invalidateQueries(["appointments"]);
      },
    });
  };
  const { data, isLoading } = useQuery(
    ["appointment", appointmentId],
    () => getAppointment(locationId, appointmentId),
    {
      enabled: !!appointmentId,
    }
  );

  if (appointment && isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin size="large" />
      </div>
    );
  }
  return (
    <div className="relative">
      {step > 1 && step < 5 ? (
        <Button
          type="link"
          htmlType="button"
          onClick={() =>
            setStep((prev: number) => (prev > 1 ? prev - 1 : prev))
          }
          className={`${
            currentLang === "ar" ? "left-0" : "right-0"
          } top-0 right-0 flex items-center text-sm text-gray`}
        >
          {currentLang === "ar" ? <IoIosArrowForward /> : <IoIosArrowBack />}
          <span className="!text-gray-700">
            {" "}
            {intl.formatMessage({
              id: "back",
            })}
          </span>
        </Button>
      ) : null}
      <ModalHeader step={step} />
      <DateAndTimePicker
        dateAndTime={dateAndTime}
        setDateAndTime={setDateAndTime}
        step={step}
      />
      {step < 5 ? (
        <>
          <div className="text-center my-2 p-2">{divider}</div>
          <AddClient clientData={clientData} setClientData={setClientData} />
          <div className="text-center my-2 p-2">{divider}</div>
          <div className="space-y-4 max-h-[350px] overflow-y-auto">
            <ServicesAndPackages
              services={services}
              setServices={setServices}
              step={step}
              setStep={setStep}
              dateAndTime={dateAndTime}
              clientPreferredGender={clientData?.preferred_staff_gender!}
              packages={packages}
              setPackages={setPackages}
            />

            {step >= 2 ? (
              <Products
                products={products}
                setProducts={setProducts}
                productsSaved={step >= 3}
                abilityToRemoveItem={step < 4}
              />
            ) : null}
          </div>
        </>
      ) : null}
      {step >= 3 && step < 5 ? (
        <div className="text-center !mt-2 !-mb-2 p-2">{divider}</div>
      ) : null}

      {step >= 3 && step < 5 ? (
        <div className="space-y-8 mt-4 ">
          <div>
            <Tax setTax={setTax} tax={tax} />
          </div>
          {locationId && clientData?.id ? (
            <div>
              <LoyaltyDiscountSelect
                setLoyaltyDiscount={setLoyaltyDiscount}
                loyaltyDiscount={loyaltyDiscount}
                clientId={clientData?.id!}
                locationId={locationId!}
              />
            </div>
          ) : null}
          <div>
            <Discount
              discount={discount}
              setDiscount={setDiscount}
              totalPrice={
                services?.reduce(
                  (acc: any, service: any) =>
                    acc + Number(service?.price || service?.price),
                  0
                ) +
                products?.reduce(
                  (acc: any, product: any) =>
                    acc +
                    Number(product?.price) *
                      Number(product?.qty || product?.quantity),
                  0
                ) +
                packages?.reduce(
                  (acc: any, pkg: any) => acc + Number(pkg?.retail_price),
                  0
                )
              }
            />
          </div>
        </div>
      ) : null}
      {step >= 2 && step < 5 ? (
        <>
          <TotalPrice
            tax={tax}
            loyaltyDiscount={loyaltyDiscount}
            discount={discount}
            products={products}
            services={services}
            packages={packages}
          />
        </>
      ) : null}
      {step >= 3 && step < 5 ? (
        <div className="mb-6 ">
          <PaymentMethod
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            isSelectedPaymentMethod={step >= 4}
          />
        </div>
      ) : null}
      {step >= 5 && data?.data?.data ? (
        <ReservationDetails appointment={data?.data?.data} />
      ) : null}

      <div>
        <div className=" flex gap-1 w-full">
          {step === 1 ? (
            <Button
              type="primary"
              htmlType="submit"
              className=""
              block
              disabled={services.length === 0 && packages.length === 0}
              onClick={() => {
                if (!dateAndTime?.date || !dateAndTime?.startTime) {
                  message.error(
                    intl.formatMessage({
                      id: "pickDateAndTimeFirst",
                    })
                  );
                  return false;
                }
                setStep(2);
              }}
            >
              {intl.formatMessage({ id: "save", defaultMessage: "حفظ" })}{" "}
            </Button>
          ) : null}
          {step === 2 ? (
            <Button
              type="primary"
              htmlType="submit"
              className=""
              block
              disabled={!validateTeamMembers()}
              // disabled={services?.length === 0}
              onClick={() => {
                if (!validateTeamMembers()) {
                  message.error(
                    intl.formatMessage({
                      id: "teamMemberRequired",
                      defaultMessage: "يجب تحديد عضو  من فريق العمل",
                    })
                  );
                  setStep(2);
                  return false;
                }

                setStep(3);
              }}
            >
              {intl.formatMessage({
                id: "continueToPayment",
                defaultMessage: "المتابعة الى الدفع",
              })}{" "}
            </Button>
          ) : null}

          {step === 3 ? (
            <Button
              type="primary"
              htmlType="submit"
              className=""
              block
              disabled={!validateTeamMembers() || !paymentMethod}
              onClick={() => {
                if (!validateTeamMembers()) {
                  message.error(
                    intl.formatMessage({
                      id: "teamMemberRequired",
                      defaultMessage: "يجب تحديد عضو  من فريق العمل",
                    })
                  );
                  setStep(2);
                  return false;
                }
                if (!tax) {
                  message.error(
                    intl.formatMessage({
                      id: "taxRequired",
                      defaultMessage: "يجب تحديد الضريبة",
                    })
                  );
                  return false;
                }
                setStep(4);
              }}
            >
              {intl.formatMessage({
                id: "continue",
                defaultMessage: "المتابعة",
              })}
            </Button>
          ) : null}

          {step === 5 && appointmentId ? (
            <div className="w-full ">
              <AppointmentModalFooter
                closeModal={() => {
                  setIsModalOpen(false);
                }}
                appointment={data?.data?.data || undefined}
                appointmentId={appointmentId}
                data={data?.data}
              />
            </div>
          ) : null}
          {step === 4 ? (
            <Button
              type="primary"
              htmlType="submit"
              className=""
              block
              disabled={!validateTeamMembers()}
              onClick={() => {
                handleSubmitAppointment();
              }}
            >
              {intl.formatMessage({ id: "save", defaultMessage: "حفظ" })}{" "}
            </Button>
          ) : null}
          {step !== 5 ? (
            <Button
              type="primary"
              htmlType="button"
              className="w-2/4 btn-primary-not"
              onClick={() => {
                setIsModalOpen(false);
              }}
            >
              {intl.formatMessage({ id: "cancel", defaultMessage: "الغاء" })}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AddAppointmentModalWrapper;
